// Couleurs des ÉLÉMENTS de gemme (énergie de combat / RPS). Reliées au
// resourceId de la gemme. Utilisé pour colorer les tirs (élément de l'arme) et
// les flashs de bouclier (élément de défense) dans la scène 3D de combat, et
// partout où l'on veut signaler un élément. Cf. seed energy_affinities + le
// mapping arme/bouclier -> élément.
//
// ResourceId connus : saphir=8, émeraude=11, diamant=12, rubis=13, topaze=14,
// élément Z=15 (hors RPS gemme), améthyste=22.

export interface ElementInfo {
  id: number;
  name: string;
  /** Couleur CSS (#rrggbb) pour l'UI (chips, halos, barres). */
  css: string;
  /** Même couleur en entier hex (0xrrggbb) pour Three.js. */
  hex: number;
}

export const ELEMENTS: Record<number, ElementInfo> = {
  8:  { id: 8,  name: 'Saphir',    css: '#3b82f6', hex: 0x3b82f6 }, // bleu
  11: { id: 11, name: 'Émeraude',  css: '#22c55e', hex: 0x22c55e }, // vert
  12: { id: 12, name: 'Diamant',   css: '#67e8f9', hex: 0x67e8f9 }, // cyan clair
  13: { id: 13, name: 'Rubis',     css: '#ef4444', hex: 0xef4444 }, // rouge
  14: { id: 14, name: 'Topaze',    css: '#f59e0b', hex: 0xf59e0b }, // orange
  15: { id: 15, name: 'Élément Z', css: '#9ca3af', hex: 0x9ca3af }, // gris (antimatière)
  22: { id: 22, name: 'Améthyste', css: '#a855f7', hex: 0xa855f7 }, // violet
};

/** Couleur neutre quand l'élément est absent (0/null) ou inconnu. */
const NEUTRAL_CSS = '#cbd5e1';
const NEUTRAL_HEX = 0xcbd5e1;

/** Couleur CSS d'un élément (gris neutre si absent/inconnu). */
export function elementColorCss(resourceId: number | null | undefined): string {
  if (!resourceId) return NEUTRAL_CSS;
  return ELEMENTS[resourceId]?.css ?? NEUTRAL_CSS;
}

/** Couleur Three.js (entier hex) d'un élément (gris neutre si absent/inconnu). */
export function elementColorHex(resourceId: number | null | undefined): number {
  if (!resourceId) return NEUTRAL_HEX;
  return ELEMENTS[resourceId]?.hex ?? NEUTRAL_HEX;
}

/** Nom FR d'un élément ('' si absent/inconnu). */
export function elementName(resourceId: number | null | undefined): string {
  if (!resourceId) return '';
  return ELEMENTS[resourceId]?.name ?? '';
}

/** Couleur d'une RESSOURCE par son CODE (carburant ou gemme-élément) : colore
 *  l'indication composant (hexagone de taille teinté = ressource utilisée). */
const RESOURCE_COLOR_BY_CODE: Record<string, string> = {
  // Gemmes (énergie de combat) — couleurs choisies par le joueur.
  topas: '#facc15',      // jaune
  saphir: '#3b82f6',     // bleu
  rubis: '#ef4444',      // rouge
  diamant: '#e5e7eb',    // blanc
  amethyste: '#a855f7',  // violet
  emeraude: '#22c55e',   // vert
  // Carburants (déplacement).
  petrole: '#b45309',    // ambre/brun
  uranium: '#84cc16',    // vert lime (radioactif)
  deuterium: '#38bdf8',  // cyan
  element_z: '#c084fc',  // violet clair (antimatière)
};

/** Couleur CSS d'une ressource par code (gris neutre si absent/inconnu). */
export function resourceColorByCode(code: string | null | undefined): string {
  if (!code) return NEUTRAL_CSS;
  return RESOURCE_COLOR_BY_CODE[code] ?? NEUTRAL_CSS;
}
